import { useCallback, useEffect, useRef, useState } from "react";

// Minimal data-fetching hook — the portal is four read-only screens, so a
// query library would be more dependency than feature. Handles unmount races
// via a generation counter; 401s never reach here (the API client clears the
// token and hard-navigates to /sign-in).

export interface ApiState<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
}

export function useApi<T>(fetcher: () => Promise<T>, deps: readonly unknown[]): ApiState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const generation = useRef(0);

  const run = useCallback(() => {
    const gen = ++generation.current;
    setLoading(true);
    setError(null);
    fetcher()
      .then((result) => {
        if (generation.current !== gen) return;
        setData(result);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (generation.current !== gen) return;
        setError(err instanceof Error ? err.message : "Something went wrong");
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    run();
    return () => {
      generation.current += 1; // invalidate in-flight result on dep change/unmount
    };
  }, [run]);

  return { data, error, loading, reload: run };
}
